import { TuiDialogService } from '@taiga-ui/core';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import {
  PdfViewerOptions,
  PdfViewerService,
} from '@core/services/pdf-viewer.service';

/**
 * Компонент-хост для предпросмотра PDF.
 *
 * Всегда открывает pdf.js viewer в диалоге. Это даёт одинаковое
 * поведение на desktop и mobile и не зависит от iframe-рендеринга браузера.
 */
@Component({
  selector: 'x-pdf-viewer-dialog',
  standalone: true,
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfViewerDialogComponent implements OnInit {
  private readonly pdfViewerService = inject(PdfViewerService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly destroyRef = inject(DestroyRef);

  private currentSource: string | Blob | null = null;
  private currentFilename = 'document.pdf';

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => this.cleanupCurrentDocument());

    this.pdfViewerService.open$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options: PdfViewerOptions) => {
        this.setCurrentDocument(options);
        void this.openPdfViewer();
      });
  }

  private setCurrentDocument(options: PdfViewerOptions): void {
    this.currentSource = options.source;
    this.currentFilename = options.filename;
  }

  private async openPdfViewer(): Promise<void> {
    if (!this.currentSource) {
      return;
    }

    const {
      PdfjsViewerDialogContentComponent: pdfjsViewerDialogContentComponent,
    } = await import('./pdfjs-viewer-dialog-content.component');

    this.dialogService
      .open(new PolymorpheusComponent(pdfjsViewerDialogContentComponent), {
        label: this.currentFilename,
        appearance: 'pdf-viewer fullscreen',
        closable: true,
        dismissible: true,
        data: {
          source: this.currentSource,
          filename: this.currentFilename,
        } satisfies PdfViewerOptions,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        complete: () => this.cleanupCurrentDocument(),
      });
  }

  private cleanupCurrentDocument(): void {
    if (
      typeof this.currentSource === 'string' &&
      this.currentSource.startsWith('blob:')
    ) {
      URL.revokeObjectURL(this.currentSource);
    }

    this.currentSource = null;
  }
}
